import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useDataSync, useAutoRefresh } from "../lib/dataSync";
import { StatCardSkeleton } from "../components/Skeletons";
import { Building2, Users, MessageSquare, TrendingUp, Crown, Star, ShieldCheck, Clock, Download, Database, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

// v2.71.7 — Clickable navigation StatCard
// Each card now acts as a quick nav into the relevant Admin tab (and applies
// a filter via the URL where possible). Falls back to a non-clickable card
// when no `to` prop is supplied (keeps surface neutral for read-only metrics
// like "Sold this month").
const StatCard = ({ icon: Icon, label, value, accent, testid, to, nav }) => {
  const isLink = !!to;
  const className = `group bg-white border ${accent ? "border-[#0055FF]" : "border-[#E4E4E7]"} p-5 rounded-sm relative ${
    isLink ? "cursor-pointer hover:border-[#0055FF] hover:shadow-md transition-all hover:-translate-y-0.5" : ""
  }`;
  const onClick = isLink ? () => nav(to) : undefined;
  const onKeyDown = isLink ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); nav(to); } } : undefined;
  return (
    <div
      className={className}
      data-testid={testid}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role={isLink ? "button" : undefined}
      tabIndex={isLink ? 0 : undefined}
      aria-label={isLink ? `Open ${label}` : undefined}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={accent ? "text-[#0055FF]" : "text-[#52525B]"} />
        <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B]">{label}</div>
        {isLink && (
          <ArrowUpRight size={12} className="ml-auto text-[#A1A1AA] group-hover:text-[#0055FF] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
        )}
      </div>
      <div className={`font-heading font-bold text-3xl font-mono-data ${accent ? "text-[#0055FF]" : "text-[#09090B]"}`}>
        {value}
      </div>
    </div>
  );
};

export default function AdminOverviewTab() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);
  const [backingUp, setBackingUp] = useState(false);

  // v2.72 — Re-usable loader. Background failures (auto-refresh during a
  // network blip) never blank an already-rendered dashboard.
  const hasDataRef = useRef(false);
  const load = useCallback(() => {
    api.get("/admin/overview")
      .then((r) => { hasDataRef.current = true; setStats(r.data); setErr(null); })
      .catch((e) => { if (!hasDataRef.current) setErr(e?.message || "Failed"); });
  }, []);
  useEffect(() => { load(); }, [load]);
  // Counts refresh instantly after any mutation anywhere + every 45 s in
  // the background (only while the tab is visible) — never stale.
  useDataSync(["properties", "users", "leads", "enquiries", "projects", "dashboard"], load);
  useAutoRefresh(load, 45000);

  // v2.46 — One-click full DB backup. Downloads as JSON.
  const downloadBackup = async () => {
    setBackingUp(true);
    try {
      const r = await api.get("/admin/backup/export");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `houseentry-backup-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Backup downloaded — ${Object.values(r.data.counts || {}).reduce((s, n) => s + (n || 0), 0)} records`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Backup failed");
    } finally {
      setBackingUp(false);
    }
  };

  if (err) return <div className="p-8 text-[#A1A1AA]">{err}</div>;
  if (!stats) {
    return (
      <div className="space-y-6" data-testid="admin-overview-loading">
        {[0, 1, 2].map((s) => (
          <div key={s} className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => <StatCardSkeleton key={i} />)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#52525B] mb-3">Properties</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard nav={nav} to="/admin?tab=listings" icon={Building2} label="Total" value={stats.properties.total} accent testid="stat-prop-total" />
          <StatCard nav={nav} to="/admin?tab=pending" icon={Clock} label="Pending" value={stats.properties.pending} testid="stat-prop-pending" />
          <StatCard nav={nav} to="/admin?tab=listings&status=Sold" icon={ShieldCheck} label="Sold (this month)" value={stats.properties.sold_this_month} testid="stat-prop-sold-month" />
          <StatCard nav={nav} to="/admin?tab=listings&premium=true" icon={Crown} label="Premium" value={stats.properties.premium} testid="stat-prop-premium" />
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#52525B] mb-3">Users</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard nav={nav} to="/admin?tab=users" icon={Users} label="Total Users" value={stats.users.total} accent testid="stat-users-total" />
          <StatCard nav={nav} to="/admin?tab=dealers" icon={Users} label="Dealers" value={stats.users.dealers} testid="stat-users-dealers" />
          <StatCard nav={nav} to="/admin?tab=users&role=client" icon={Users} label="Clients" value={stats.users.clients} testid="stat-users-clients" />
          <StatCard nav={nav} to="/admin?tab=users&filter=active_subs" icon={Star} label="Active Subscriptions" value={stats.users.active_subscriptions} testid="stat-users-active-subs" />
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#52525B] mb-3">Enquiries</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard nav={nav} to="/admin?tab=crm" icon={MessageSquare} label="Total" value={stats.enquiries.total} testid="stat-enq-total" />
          <StatCard nav={nav} to="/admin?tab=crm&status=open" icon={TrendingUp} label="New / Open" value={stats.enquiries.new} accent testid="stat-enq-new" />
          <StatCard nav={nav} to="/admin?tab=listings&status=Sold" icon={ShieldCheck} label="Sold (all-time)" value={stats.properties.sold_total} testid="stat-prop-sold-total" />
          <StatCard nav={nav} to="/admin?tab=listings&best_deal=true" icon={Star} label="Best Deals" value={stats.properties.best_deal} testid="stat-prop-best" />
        </div>
      </div>

      {/* v2.46 — Backup & Safety */}
      <div className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid="admin-backup-block">
        <div className="flex items-start gap-3">
          <Database size={18} className="text-[#0055FF] mt-1" />
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#52525B] mb-1">Database Safety</div>
            <h3 className="font-heading font-semibold text-base mb-1">Backup &amp; Restore</h3>
            <p className="text-[12px] text-[#52525B] mb-3">
              Download a full JSON snapshot of users, properties, projects, leads, enquiries and team data.
              Use before any deploy or risky migration.
            </p>
            <button
              onClick={downloadBackup}
              disabled={backingUp}
              className="inline-flex items-center gap-2 h-9 px-4 bg-[#0055FF] hover:bg-[#0044CC] text-white text-[13px] font-semibold rounded-sm disabled:opacity-50 transition-colors"
              data-testid="admin-backup-download-btn"
            >
              <Download size={13} />
              {backingUp ? "Preparing snapshot…" : "Download Backup (.json)"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
